import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { GetAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

const UpdateOrganizationEnforce2FA = graphql(/* GraphQL */ `
  mutation UpdateOrganizationEnforce2FA(
    $input: UpdateOrganizationEnforce2FAInput!
  ) {
    updateOrganizationEnforce2FA(input: $input) {
      __typename
      ... on EmptySuccess {
        _empty
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type UpdateOrganizationEnforce2FAResult = {
  updateEnforce2FA: (args: {
    organizationId: string
    enforce2FA: boolean
  }) => Promise<boolean>
  loading: boolean
}

export const useUpdateOrganizationEnforce2FA =
  (): UpdateOrganizationEnforce2FAResult => {
    const [mutate, { loading }] = useMutation(UpdateOrganizationEnforce2FA)

    const updateEnforce2FA = useCallback(
      async ({
        organizationId,
        enforce2FA,
      }: {
        organizationId: string
        enforce2FA: boolean
      }): Promise<boolean> => {
        try {
          const { data, error } = await mutate({
            variables: { input: { organizationId, enforce2FA } },
            refetchQueries: [GetAccount],
          })

          if (error) {
            throw new Error(error.message ?? 'Unexpected API response')
          }

          const result = data?.updateOrganizationEnforce2FA
          if (result?.__typename === 'VoidMutationError') {
            throw new Error(result.message)
          }
          if (result?.__typename !== 'EmptySuccess') {
            throw new Error('Failed to update 2FA enforcement')
          }

          toast.success(
            enforce2FA
              ? '2FA is now required for all members'
              : '2FA is no longer required for all members',
          )
          return true
        } catch (error) {
          toast.error(
            'There was an error updating 2FA enforcement. Please try again.',
          )
          const normalizedError =
            error instanceof Error ? error : new Error(String(error))
          logError(normalizedError, {
            metaData: { organizationId, enforce2FA },
          })
          throw normalizedError
        }
      },
      [mutate],
    )

    return { updateEnforce2FA, loading }
  }
